import { z } from 'zod';
import { NotificationTopicSchema, TRANSACTIONAL_TOPICS, UNTOGGLEABLE_TOPICS, } from './notification-events';
/**
 * Per-user notification preferences (Phase 8) — the `user_notification_preferences`
 * collection, one document per user, keyed by `userId` (unique index).
 *
 * The preference is a GATE, not decoration:
 *
 *  - `inapp` can never be switched off for a TRANSACTIONAL topic (bids / kyc /
 *    deals / account). Losing a lot to a silent preference is a support
 *    incident, not a feature, so the in-app feed always records it.
 *  - `marketing` is OPT-IN: absent preferences mean "no", never "yes".
 *  - `consentAt` is stamped by the SERVER the moment a channel goes from off to
 *    on, and is never read from the client payload — an audit trail the client
 *    cannot forge (that is why {@link UpdateNotificationPreferencesSchema},
 *    which is what the PUT body is parsed with, has no `consentAt` at all).
 */
export const NotificationChannelNameSchema = z.enum([
    'email',
    'telegram',
    'whatsapp',
    'push',
    'inapp',
    'admin-telegram',
    // mobile-app-expo phase 3: Expo push to the native app. Not a separate user
    // toggle — it is gated by the `push` channel preference (see
    // {@link preferenceChannelOf}), so "push off" silences browser AND phone.
    'mobile-push',
    // admin-mobile-app phase 5: Expo push to the ADMIN app's device sessions
    // (`admin_device_sessions.pushToken`). Staff routing — no user preference.
    'admin-mobile-push',
]);
/** The channels a USER can configure. `admin-telegram` / `admin-mobile-push` are staff-only routing. */
export const UserChannelNameSchema = z.enum(['email', 'telegram', 'whatsapp', 'push', 'inapp']);
/**
 * The user preference that gates a delivery channel. `mobile-push` has no
 * toggle of its own and rides `push`; `admin-telegram` and `admin-mobile-push`
 * are staff routing and have none (null).
 */
export function preferenceChannelOf(channel) {
    if (channel === 'mobile-push')
        return 'push';
    if (channel === 'admin-telegram' || channel === 'admin-mobile-push')
        return null;
    return channel;
}
export const ChannelPreferenceSchema = z.object({
    enabled: z.boolean(),
    /** Server-stamped instant the channel was last switched ON. */
    consentAt: z.coerce.date().nullish(),
});
export const NotificationChannelsPreferenceSchema = z.object({
    email: ChannelPreferenceSchema,
    telegram: ChannelPreferenceSchema,
    whatsapp: ChannelPreferenceSchema,
    push: ChannelPreferenceSchema,
    inapp: ChannelPreferenceSchema,
});
/**
 * Toggleable topics only. `account` is deliberately absent — see
 * UNTOGGLEABLE_TOPICS.
 */
export const NotificationTopicsPreferenceSchema = z.object({
    bids: z.boolean(),
    kyc: z.boolean(),
    deals: z.boolean(),
    marketing: z.boolean(),
    /**
     * mobile-app-expo phase 3. OPTIONAL so every document stored before it
     * still parses; absent reads as the default (ON) in {@link allowsNotification}.
     */
    saved_search: z.boolean().optional(),
});
export const UserNotificationPreferencesSchema = z.object({
    userId: z.string().min(1),
    channels: NotificationChannelsPreferenceSchema,
    topics: NotificationTopicsPreferenceSchema,
    updatedAt: z.coerce.date().nullish(),
});
/**
 * The PUT body. Deliberately narrower than the stored document:
 *  - no `userId` (the server takes it from the session — IDOR-proof by shape);
 *  - no `consentAt` (server-stamped);
 *  - every field optional so a screen can save one toggle.
 */
export const UpdateNotificationPreferencesSchema = z.object({
    channels: z
        .object({
        email: z.object({ enabled: z.boolean() }).optional(),
        telegram: z.object({ enabled: z.boolean() }).optional(),
        whatsapp: z.object({ enabled: z.boolean() }).optional(),
        push: z.object({ enabled: z.boolean() }).optional(),
        inapp: z.object({ enabled: z.boolean() }).optional(),
    })
        .optional(),
    topics: z
        .object({
        bids: z.boolean().optional(),
        kyc: z.boolean().optional(),
        deals: z.boolean().optional(),
        marketing: z.boolean().optional(),
        saved_search: z.boolean().optional(),
    })
        .optional(),
});
/**
 * Defaults for a user who has never opened the preferences screen.
 *
 * `telegram` and `push` default ON because their REAL opt-in happens
 * elsewhere and is unforgeable: a Telegram chat only receives anything after
 * the user ran /start with a minted link token, and a push subscription only
 * exists after the browser permission prompt. Both channels are a no-op
 * without that, so defaulting them on cannot message anyone who did not ask.
 *
 * `email` and `whatsapp` default OFF: there is no such per-user act of consent
 * for an address we already hold, so they stay opt-in.
 */
export function defaultNotificationPreferences(userId) {
    return {
        userId,
        channels: {
            email: { enabled: false, consentAt: null },
            telegram: { enabled: true, consentAt: null },
            whatsapp: { enabled: false, consentAt: null },
            push: { enabled: true, consentAt: null },
            inapp: { enabled: true, consentAt: null },
        },
        topics: { bids: true, kyc: true, deals: true, marketing: false, saved_search: true },
        updatedAt: null,
    };
}
/** Defaults per toggleable topic — the same values {@link defaultNotificationPreferences} stores. */
const DEFAULT_TOPICS = defaultNotificationPreferences('').topics;
/** True when the topic can never be silenced on the in-app feed. */
export function isTransactionalTopic(topic) {
    return TRANSACTIONAL_TOPICS.includes(topic);
}
/**
 * THE gate. Pure, so the same decision is testable without a database and
 * identical in the dispatcher and in any future preview screen.
 *
 * Order matters:
 *  1. in-app + transactional topic  -> always allowed (cannot be silenced);
 *  2. topic switched off            -> denied (marketing defaults to off);
 *  3. channel switched off          -> denied.
 */
export function allowsNotification(prefs, channel, topic) {
    if (channel === 'inapp' && isTransactionalTopic(topic))
        return true;
    if (!UNTOGGLEABLE_TOPICS.includes(topic)) {
        const parsedTopic = NotificationTopicSchema.parse(topic);
        if (parsedTopic !== 'account') {
            const topics = prefs.topics;
            // A topic added after a document was stored (saved_search) is absent
            // from it: read the documented default, never "off" and never "on" by accident.
            const stored = topics[parsedTopic];
            const enabled = stored ?? DEFAULT_TOPICS[parsedTopic];
            if (enabled !== true)
                return false;
        }
    }
    return prefs.channels[channel].enabled === true;
}
//# sourceMappingURL=notification-preferences.js.map